import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
    Users, Building, FileText, Activity, PieChart, BarChart2, Bell, CheckCircle, AlertTriangle, Info
} from 'lucide-react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { 
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, 
    BarElement, Title, Tooltip, Legend, ArcElement, Filler 
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, Filler);

const Dashboard = () => {
    const [stats, setStats] = useState({ doctors_count: 0, patients_count: 0, hospitals_count: 0, recipes_count: 0 });
    const [activityData, setActivityData] = useState({ labels: [], recipes: [], patients: [] });
    const [statusData, setStatusData] = useState({ active: 0, completed: 0 });
    const [topMedicines, setTopMedicines] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            try {
                const [statsRes, chartsRes, recipesRes, alertsRes] = await Promise.all([
                    api.get('/stats').catch(() => ({ data: {} })),
                    api.get('/charts/dashboard').catch(() => ({ data: {} })),
                    api.get('/prescriptions').catch(() => ({ data: [] })),
                    api.get('/alerts').catch(() => ({ data: { logs: [] } }))
                ]);

                setStats(statsRes.data || { doctors_count: 0, patients_count: 0, hospitals_count: 0, recipes_count: 0 });
                
                if (chartsRes.data) {
                    setActivityData(chartsRes.data.activity || { labels: [], recipes: [], patients: [] });
                    setStatusData(chartsRes.data.status || { active: 0, completed: 0 });
                }

                if (recipesRes.data && recipesRes.data.length > 0) {
                    const medCounts = {};
                    recipesRes.data.forEach(p => {
                        const med = p.medication || 'Не указано';
                        medCounts[med] = (medCounts[med] || 0) + 1;
                    });
                    
                    const sortedTop5 = Object.entries(medCounts)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([label, value]) => ({
                            label: label.length > 25 ? label.slice(0, 25) + '...' : label,
                            value: value
                        }));
                    setTopMedicines(sortedTop5);
                }

                if (alertsRes.data && alertsRes.data.logs) {
                    setAlerts(alertsRes.data.logs.slice(0, 3));
                }

            } catch (error) {
                console.error("Ошибка загрузки дашборда:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAllData();
    }, []);

    const chartActivity = {
        labels: activityData.labels || [],
        datasets: [
            {
                label: 'Рецепты',
                data: activityData.recipes || [],
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.3,
                fill: true
            },
            {
                label: 'Пациенты',
                data: activityData.patients || [],
                borderColor: '#8b5cf6',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                tension: 0.3,
                fill: true
            }
        ]
    };

    const chartStatus = {
        labels: ['В процессе (Активные)', 'Выдано (Завершенные)'],
        datasets: [{
            data: [statusData.active, statusData.completed],
            backgroundColor: ['#fbbf24', '#22c55e'],
            hoverOffset: 4,
            borderWidth: 0
        }]
    };

    const chartMedicines = {
        labels: topMedicines.map(m => m.label),
        datasets: [{
            label: 'Назначений',
            data: topMedicines.map(m => m.value),
            backgroundColor: '#10b981',
            borderRadius: 6
        }]
    };

    const optionsLine = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: true, grid: { color: '#f3f4f6' } }, x: { grid: { display: false } } } };
    const optionsDoughnut = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };
    const optionsBar = { responsive: true, maintainAspectRatio: false, indexAxis: 'y', scales: { x: { grid: { color: '#f3f4f6' } }, y: { grid: { display: false } } } };

    const getAlertIcon = (level) => {
        switch(level) {
            case 'error': return <AlertTriangle className="text-red-600" size={20} color="#dc2626"/>;
            case 'warning': return <AlertTriangle className="text-orange-500" size={20} color="#f59e0b"/>;
            case 'success': return <CheckCircle className="text-green-600" size={20} color="#16a34a"/>;
            default: return <Info className="text-blue-500" size={20} color="#3b82f6"/>;
        }
    };

    const formatTime = (dateString) => {
        const d = new Date(dateString);
        return `${d.toLocaleDateString('ru-RU')} в ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
    };

    if (isLoading) return <div style={{padding:'40px', textAlign:'center', color:'#6b7280'}}>Загрузка панели управления...</div>;

    return (
        <div>
            <div className="stats-grid">
                <div className="card stat-blue">
                    <div className="card-header">
                        <h4 className="card-title"><Users className="text-blue" size={16} /> Всего врачей</h4>
                    </div>
                    <div className="card-body">
                        <div className="stat-value text-blue">{stats.doctors_count || 0}</div>
                        <p className="stat-desc">Зарегистрировано в системе</p>
                    </div>
                </div>
                <div className="card stat-green">
                    <div className="card-header">
                        <h4 className="card-title"><Building className="text-green" size={16} /> Всего больниц</h4>
                    </div>
                    <div className="card-body">
                        <div className="stat-value text-green">{stats.hospitals_count || 0}</div>
                        <p className="stat-desc">Активных учреждений</p>
                    </div>
                </div>
                <div className="card stat-purple">
                    <div className="card-header">
                        <h4 className="card-title"><Users className="text-purple" size={16} /> Всего пациентов</h4>
                    </div>
                    <div className="card-body">
                        <div className="stat-value text-purple">{stats.patients_count || 0}</div>
                        <p className="stat-desc">В медицинской базе</p>
                    </div>
                </div>
                <div className="card stat-orange">
                    <div className="card-header">
                        <h4 className="card-title"><FileText className="text-orange" size={16} /> Всего рецептов</h4>
                    </div>
                    <div className="card-body">
                        <div className="stat-value text-orange">{stats.recipes_count || 0}</div>
                        <p className="stat-desc">Выписано врачами</p>
                    </div>
                </div>
            </div>

            <div className="charts-grid-2" style={{ marginTop: '1.5rem' }}>
                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title"><Activity className="text-blue" size={20} /> Активность за неделю</h4>
                        <p className="card-subtitle">Рецепты и пациенты по дням</p>
                    </div>
                    <div className="card-body chart-wrapper">
                        <Line data={chartActivity} options={optionsLine} />
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title"><PieChart className="text-orange" size={20} /> Статус рецептов</h4>
                        <p className="card-subtitle">Распределение по статусам</p>
                    </div>
                    <div className="card-body chart-wrapper-center">
                        <div style={{ height: '300px', width: '100%' }}>
                            <Doughnut data={chartStatus} options={optionsDoughnut} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="charts-grid-2" style={{ marginTop: '1.5rem' }}>
                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title"><BarChart2 className="text-green" size={20} /> Топ-5 назначаемых лекарств</h4>
                        <p className="card-subtitle">Самые популярные препараты за все время</p>
                    </div>
                    <div className="card-body chart-wrapper">
                        {topMedicines.length > 0 ? (
                            <Bar data={chartMedicines} options={optionsBar} />
                        ) : (
                            <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'#9ca3af'}}>Нет данных о рецептах</div>
                        )}
                    </div>
                </div>

                <div className="card border-red">
                    <div className="card-header">
                        <h4 className="card-title text-red" style={{ color: 'var(--primary-red)' }}>
                            <Bell size={20} /> Последние уведомления
                        </h4>
                        <p className="card-subtitle">Важные системные события и ошибки</p>
                    </div>
                    <div className="card-body" style={{maxHeight: '300px', overflowY: 'auto', padding: '0 16px 0', marginBottom: '16px' }}>
                        <div className="notifications-list" style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                            {(!alerts || alerts.length === 0) ? (
                                <p style={{color: '#999', textAlign: 'center', padding: '20px'}}>Журнал пуст</p>
                            ) : (
                                alerts.map(log => (
                                    <div key={log.id} className="notify-item" style={{background: '#f8fafc', border: '1px solid #e5e7eb', padding: '12px', borderRadius: '8px', display: 'flex', gap: '12px', alignItems: 'flex-start'}}>
                                        <div style={{marginTop: '2px'}}>{getAlertIcon(log.level)}</div>
                                        <div>
                                            <p className="notify-title" style={{fontWeight: '600', color: '#111827', fontSize: '0.95rem', margin: '0 0 4px 0'}}>{log.category || 'Уведомление'}</p>
                                            <p className="notify-desc" style={{fontSize: '0.85rem', color: '#4b5563', margin: '0 0 4px 0'}}>{log.message}</p>
                                            <p style={{fontSize: '0.75rem', color: '#9ca3af', margin: 0}}>{formatTime(log.created_at)}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;